import { parsePrice } from "./formatters";
import type { ManageSubscriptionSheetProduct } from "./types";

export type SubscriptionLinePriceFields = Pick<
  ManageSubscriptionSheetProduct,
  | "price"
  | "order_line_item_price"
  | "subscription_unit_price"
  | "subscription_one_time_price"
  | "subscription_discounted_price"
  | "subscription_discounts"
>;

function finitePrice(
  v: string | number | null | undefined,
): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/**
 * Per-unit subscription net vs compare-at (one-time) price.
 *
 * - Retention / code on the contract: retail `price` vs `subscription_discounted_price`.
 * - Otherwise: subscription unit (or billed line) vs one-time (`subscription_one_time_price` ?? `price`).
 */
export function subscriptionUnitPrices(
  item: SubscriptionLinePriceFields,
): { net: number | null; original: number | null } {
  const retentionClaimed = (item.subscription_discounts?.length ?? 0) > 0;

  if (retentionClaimed) {
    const { display, original } = parsePrice(
      item.price,
      item.subscription_discounted_price ??
        item.order_line_item_price ??
        null,
    );
    return { net: display, original };
  }

  const net =
    finitePrice(item.subscription_discounted_price) ??
    finitePrice(item.subscription_unit_price) ??
    finitePrice(item.order_line_item_price) ??
    finitePrice(item.price);

  const original =
    finitePrice(item.subscription_one_time_price) ?? finitePrice(item.price);

  return { net, original };
}

/**
 * The money the customer actually pays, per line and per contract.
 *
 * This is THE pricing path for the manage-subscription surface. The cancel chat
 * quotes savings from it, and the manage sheet displays a total from it, so they
 * must be the same number: the customer reads the sheet's total, presses Cancel,
 * and is then told what they'd save. A second, independently-derived figure here
 * would contradict the screen they were just looking at.
 */

/** Per-line subscription total (unit × quantity), net and original, discount-aware. */
export function lineTotals(
  item: SubscriptionLinePriceFields & { quantity?: number },
): {
  qty: number;
  net: number | null;
  original: number | null;
} {
  const { net: unitNet, original: unitOriginal } =
    subscriptionUnitPrices(item);
  const qty = item.quantity != null && item.quantity > 0 ? item.quantity : 1;
  const net =
    unitNet != null && Number.isFinite(unitNet) ? unitNet * qty : null;
  const orig =
    unitOriginal != null && Number.isFinite(unitOriginal)
      ? unitOriginal * qty
      : null;
  return { qty, net, original: orig };
}

function sumLineTotals(
  lines: readonly ManageSubscriptionSheetProduct[],
  pick: "net" | "original",
): number | null {
  const vals = lines
    .map((l) => lineTotals(l)[pick])
    .filter((n): n is number => n != null);
  if (vals.length === 0) return null;
  return Math.round(vals.reduce((sum, n) => sum + n, 0) * 100) / 100;
}

/**
 * What the WHOLE subscription costs per delivery. Null when no line carries a
 * usable price — say nothing rather than invent a figure the invoice won't match.
 */
export function contractPerDeliveryTotal(
  lines: readonly ManageSubscriptionSheetProduct[],
): number | null {
  return sumLineTotals(lines, "net");
}

/**
 * Combined one-time (non-subscription) price across every line — what the
 * switch-to-one-time fee is measured against on a whole-contract conversion.
 */
export function contractOneTimeTotal(
  lines: readonly ManageSubscriptionSheetProduct[],
): number | null {
  return sumLineTotals(lines, "original");
}

/** Mean days in a month — good enough to turn a weekly cadence into a monthly one. */
const DAYS_PER_MONTH = 30.44;

/** Months per billing cycle, or null when the unit is unrecognised. */
export function monthsPerCycle(
  cadence: { interval: number; intervalUnit: string } | null,
): number | null {
  if (!cadence) return null;
  const { interval, intervalUnit } = cadence;
  if (!Number.isFinite(interval) || interval < 1) return null;
  switch (intervalUnit.trim().toLowerCase()) {
    case "day":
      return interval / DAYS_PER_MONTH;
    case "week":
      return (interval * 7) / DAYS_PER_MONTH;
    case "month":
      return interval;
    case "year":
      return interval * 12;
    default:
      return null;
  }
}

/** What the whole subscription costs per month at a given cadence. */
export function monthlyEquivalent(
  total: number | null,
  cadence: { interval: number; intervalUnit: string } | null,
): number | null {
  const months = monthsPerCycle(cadence);
  if (total == null || months == null || months <= 0) return null;
  return total / months;
}
